import { useState } from 'react'
import moment from 'moment'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'

import {
  formatCurrency,
  formatDateTime,
  formatNumber,
  getTimezoneDate,
  timezones,
} from '../../utilities/helpers'

import {
  exportDynamicPricingSettlementExcel,
  getFeeColumnLabel,
} from './dynamicPricingSettlementExcelExport'

import './dynamicPricingSettlementReport.scss'

function ReportMetaItem({ label, value }) {
  return (
    <div className="dynamic-pricing-settlement-report__meta-item">
      <span className="dynamic-pricing-settlement-report__meta-label">
        {label}
      </span>
      <span className="dynamic-pricing-settlement-report__meta-value">
        {value}
      </span>
    </div>
  )
}

export default function DynamicPricingSettlementReport({
  data,
  isLoading,
  variant = 'actual',
  organizationName = '',
}) {
  const isPreview = variant === 'preview' || data?.preview
  const [isExporting, setIsExporting] = useState(false)

  if (isLoading) {
    return (
      <Card
        body
        className="card--md card-with-border dynamic-pricing-settlement-report"
      >
        <p className="mb-0 text-muted">
          {isPreview
            ? 'Loading projected settlement report…'
            : 'Loading settlement report…'}
        </p>
      </Card>
    )
  }

  if (!data || data.error) {
    return (
      <Card
        body
        className="card--md card-with-border dynamic-pricing-settlement-report"
      >
        <p className="mb-0 text-muted">
          {isPreview
            ? 'Unable to load projected settlement report.'
            : 'Unable to load settlement report.'}
        </p>
      </Card>
    )
  }

  const event = data.event
  const venue = event?.venue
  const venueLine = [venue?.name, venue?.city, venue?.state]
    .filter(Boolean)
    .join(' • ')
  const eventStart = event?.start
    ? formatDateTime(
        getTimezoneDate(event.start, timezones(venue?.timezone)),
        'dateTime'
      )
    : '—'

  const feeColumnLabel = getFeeColumnLabel(data.summary)

  const handleExcelExport = async () => {
    if (!data?.rows?.length || isExporting) return
    setIsExporting(true)
    try {
      await exportDynamicPricingSettlementExcel({
        data,
        organizationName,
        isPreview,
      })
    } catch (err) {
      console.error('Dynamic pricing settlement Excel export failed:', err)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Card
      body
      className="card--md card-with-border dynamic-pricing-settlement-report"
      data-testid="dynamic-pricing-settlement-report"
    >
      <div className="dynamic-pricing-settlement-report__banner">
        <div className="dynamic-pricing-settlement-report__banner-main">
          <h2>
            {isPreview
              ? 'Projected Dynamic Pricing Settlement'
              : 'Dynamic Pricing Settlement Report'}
          </h2>
          {isPreview && (
            <p className="dynamic-pricing-settlement-report__preview-note mb-0">
              Estimated from preview prices and sold quantities in pricing level
              snapshots. No orders or prices were changed.
            </p>
          )}
        </div>
        <Button
          variant="outline-light"
          size="sm"
          className="icon-button btn-download dynamic-pricing-settlement-report__export"
          disabled={!data.rows?.length || isExporting}
          onClick={handleExcelExport}
        >
          {isExporting ? 'Exporting…' : 'Excel'}
        </Button>
      </div>

      <div className="dynamic-pricing-settlement-report__meta">
        <ReportMetaItem label="Event" value={event?.name || '—'} />
        <ReportMetaItem label="Date / Time" value={eventStart} />
        <ReportMetaItem label="Venue" value={venueLine || '—'} />
        <ReportMetaItem label="Report Code" value={event?.reportCode || '—'} />
        <ReportMetaItem
          label="Generated"
          value={moment(data.generatedAt).format('DD MMM YYYY h:mm A')}
        />
      </div>

      {!data.feeConfig && (
        <div className="dynamic-pricing-settlement-report__notice">
          No dynamic pricing fee rule found in organization fee config. Add a
          rule with <code>productType: dynamic_pricing</code> (for example, BT
          Fee 5%).
        </div>
      )}

      {data.rows?.length ? (
        <>
          <div className="table-responsive">
            <Table className="dynamic-pricing-settlement-report__table">
              <thead>
                <tr>
                  <th>Price Level</th>
                  <th>Pricing Level</th>
                  <th>Face Value</th>
                  <th>Qty</th>
                  <th>Total Face Value</th>
                  <th>Total Dynamic</th>
                  <th>Lift</th>
                  <th>{feeColumnLabel}</th>
                  <th>Net Lift</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={`${row.pricingLevelName}-${row.faceValue}`}>
                    <td>{row.priceLevel}</td>
                    <td>{row.pricingLevelName}</td>
                    <td>{formatCurrency(row.faceValue)}</td>
                    <td>{formatNumber(row.qty)}</td>
                    <td>{formatCurrency(row.totalFaceValue)}</td>
                    <td>{formatCurrency(row.totalPlatinum)}</td>
                    <td>{formatCurrency(row.lift)}</td>
                    <td>{formatCurrency(row.tmFee)}</td>
                    <td>{formatCurrency(row.netLift)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan={3}>Total</td>
                  <td>{formatNumber(data.totals?.qty || 0)}</td>
                  <td>{formatCurrency(data.totals?.totalFaceValue || 0)}</td>
                  <td>{formatCurrency(data.totals?.totalPlatinum || 0)}</td>
                  <td>{formatCurrency(data.totals?.totalLift || 0)}</td>
                  <td>{formatCurrency(data.totals?.totalTmFee || 0)}</td>
                  <td>{formatCurrency(data.totals?.totalNetLift || 0)}</td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="dynamic-pricing-settlement-report__summary">
            <div className="dynamic-pricing-settlement-report__summary-row">
              <span>Dynamic Pricing Total</span>
              <strong>
                {formatCurrency(data.summary?.platinumTotal || 0)}
              </strong>
            </div>
            <div className="dynamic-pricing-settlement-report__summary-row">
              <span>{feeColumnLabel}</span>
              <strong>-{formatCurrency(data.summary?.tmFeeAmount || 0)}</strong>
            </div>
            <div className="dynamic-pricing-settlement-report__summary-row dynamic-pricing-settlement-report__summary-row--total">
              <span>
                Total Payment: Net Dynamic Amount (Face Value + Net Lift)
              </span>
              <strong>
                {formatCurrency(data.summary?.netPlatinumAmount || 0)}
              </strong>
            </div>
          </div>
        </>
      ) : (
        <div className="dynamic-pricing-settlement-report__empty">
          <p className="mb-0">
            {isPreview
              ? 'No projected lift for the current preview. Run Preview Rules after rules match live metrics, or ensure pricing level snapshots include sold quantities.'
              : 'No dynamic pricing lift sales recorded for this event yet.'}
          </p>
        </div>
      )}

      <div className="dynamic-pricing-settlement-report__notes">
        <p>
          {isPreview
            ? 'Projected quantities come from the latest pricing level daily snapshot per level. Actual settlement will reflect completed orders after prices are applied.'
            : 'Data is accurate as of the generation time and should be run alongside standard ticket sales reports.'}
        </p>
        <p>
          Lift is calculated as dynamic price revenue minus face value revenue.
          The BT fee is applied to total dynamic revenue using the organization
          fee config rule for dynamic pricing.
        </p>
        <p>Taxes and facility fees are not included in this report.</p>
      </div>
    </Card>
  )
}
